import { useEffect, useState } from 'react';
import { api, ReportResult } from '../../lib/api';
import { BarChart } from '../Charts/BarChart';
import { LineChart } from '../Charts/LineChart';
import { StackedBarChart } from '../Charts/StackedBarChart';
import { PieChart } from '../Charts/PieChart';
import { DataTable, isCurrencyColumn, isAgeColumn } from '../Charts/DataTable';
import { LoadingSpinner } from '../Common/LoadingSpinner';

interface Props {
  widget: {
    id: string;
    reportId: string;
    reportName: string;
    chartType: 'bar' | 'line' | 'stacked' | 'table' | 'pie' | 'kpi';
  };
  onRemove?: () => void;
}

// Column-name heuristics reused from DataTable: decide if a number should
// render as currency, a percentage, or a plain number for KPI cards.
function isPercentColumn(col: string): boolean {
  const lower = col.toLowerCase();
  return lower.includes('%') || lower.includes('rate') || lower.includes('utilization');
}

function formatKpi(col: string, value: any): string {
  if (value == null) return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  if (isCurrencyColumn(col) && !isAgeColumn(col)) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
  }
  if (isPercentColumn(col)) return `${n.toFixed(1)}%`;
  return new Intl.NumberFormat('en-US').format(n);
}

// KPI mode: one row from the query becomes one-or-more big-number tiles.
// Every column becomes a stat cell so a single template can populate multiple
// numbers in one widget (e.g., Total A/R = combined + patient split + insurance split).
function KpiTiles({ columns, row }: { columns: string[]; row: Record<string, any> }) {
  return (
    <div className="grid grid-flow-col auto-cols-fr gap-3 h-full items-center">
      {columns.map(c => (
        <div key={c} className="text-center">
          <div className="text-[10px] uppercase tracking-[0.7px] text-text-hint mb-1">{c}</div>
          <div className="text-[28px] leading-tight font-medium text-text-primary tabular-nums">
            {formatKpi(c, row[c])}
          </div>
        </div>
      ))}
    </div>
  );
}

export function DashboardWidget({ widget, onRemove }: Props) {
  const [result, setResult] = useState<ReportResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.runSavedReport(widget.reportId)
      .then(r => { setResult(r); setError(null); })
      .catch(err => setError(err.message ?? 'Failed to load'))
      .finally(() => setLoading(false));
  }, [widget.reportId]);

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden flex flex-col h-full">
      <div className="px-3 py-2 border-b border-border-light flex items-center justify-between">
        <div className="text-sm font-medium text-text-primary truncate">{widget.reportName}</div>
        {onRemove && (
          <button onClick={onRemove} className="text-text-muted hover:text-danger text-xs">Remove</button>
        )}
      </div>
      <div className="flex-1 overflow-auto p-3">
        {loading && <LoadingSpinner label="Loading…" />}
        {error && <div className="text-danger text-sm">{error}</div>}
        {result && result.rows.length === 0 && (
          <div className="text-xs text-text-muted">No data.</div>
        )}
        {result && result.rows.length > 0 && (() => {
          if (widget.chartType === 'kpi') {
            return <KpiTiles columns={result.columns} row={result.rows[0]} />;
          }
          const xKey = result.columns[0];
          const numericCols = result.columns.filter(c => isCurrencyColumn(c) || isPercentColumn(c) || (!isAgeColumn(c) && typeof result.rows[0][c] === 'number'));
          const yKey = numericCols[0] ?? result.columns[1];
          if (widget.chartType === 'bar' && yKey)     return <BarChart data={result.rows} xKey={xKey} yKey={yKey} height={220} />;
          if (widget.chartType === 'line' && yKey)    return <LineChart data={result.rows} xKey={xKey} yKey={yKey} height={220} />;
          if (widget.chartType === 'stacked')         return <StackedBarChart data={result.rows} xKey={xKey} stackKeys={numericCols} height={220} />;
          if (widget.chartType === 'pie' && yKey)     return <PieChart data={result.rows} nameKey={xKey} valueKey={yKey} height={260} />;
          return <DataTable columns={result.columns.slice(0, 6)} rows={result.rows.slice(0, 50)} maxHeight={220} />;
        })()}
      </div>
    </div>
  );
}
